<script setup lang="ts">
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeCross from "@dsk/icons/LeCross.vue";
import { useMediaQuery } from "@vueuse/core";
import { type Component, computed } from "vue";
import { nextTick, ref } from "vue";

import { useUniqueId } from "@/composables";
import { useDictsysInflectionSchemeStore } from "@/stores";
import type { DictsysInflectionSchemeTable } from "@/types";

import LeEnglishAdjectiveInflectionScheme from "./inflection-schemes/LeEnglishAdjectiveInflectionScheme.vue";
import LeEnglishNounInflectionScheme from "./inflection-schemes/LeEnglishNounInflectionScheme.vue";
import LeEnglishVerbInflectionScheme from "./inflection-schemes/LeEnglishVerbInflectionScheme.vue";
import LeExtraInflectionScheme from "./inflection-schemes/LeExtraInflectionScheme.vue";
import LeFrenchAdjectiveInflectionScheme from "./inflection-schemes/LeFrenchAdjectiveInflectionScheme.vue";
import LeFrenchNounInflectionScheme from "./inflection-schemes/LeFrenchNounInflectionScheme.vue";
import LeFrenchVerbInflectionScheme from "./inflection-schemes/LeFrenchVerbInflectionScheme.vue";
import LeGermanAdjectiveInflectionScheme from "./inflection-schemes/LeGermanAdjectiveInflectionScheme.vue";
import LeGermanArticleInflectionScheme from "./inflection-schemes/LeGermanArticleInflectionScheme.vue";
import LeGermanNounInflectionScheme from "./inflection-schemes/LeGermanNounInflectionScheme.vue";
import LeGermanVerbInflectionScheme from "./inflection-schemes/LeGermanVerbInflectionScheme.vue";
import LeLegacyInflectionScheme from "./inflection-schemes/LeLegacyInflectionScheme.vue";
import LeSpanishVerbInflectionScheme from "./inflection-schemes/LeSpanishVerbInflectionScheme.vue";

const props = defineProps<{ schemeId: string; language: string }>();

const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
const { id: extraInflectionTableId } = useUniqueId("le-extra-inflection-table-");
const dictsysInflectionSchemeStore = useDictsysInflectionSchemeStore();
const { closeScheme, getHeadWord, getScheme, getWordclass, isSchemeOpen } = dictsysInflectionSchemeStore;

const inflectionSchemeComponent = computed<Component | null>(() => {
  const language = props.language;
  const wordclass = getWordclass(props.schemeId).key;

  const inflectionSchemeMap: Record<string, Record<string, object>> = {
    de: {
      adjective: LeGermanAdjectiveInflectionScheme,
      article: LeGermanArticleInflectionScheme,
      noun: LeGermanNounInflectionScheme,
      verb: LeGermanVerbInflectionScheme
    },
    en: {
      adjective: LeEnglishAdjectiveInflectionScheme,
      noun: LeEnglishNounInflectionScheme,
      verb: LeEnglishVerbInflectionScheme
    },
    es: {
      verb: LeSpanishVerbInflectionScheme
    },
    fr: {
      adjective: LeFrenchAdjectiveInflectionScheme,
      noun: LeFrenchNounInflectionScheme,
      verb: LeFrenchVerbInflectionScheme
    }
  };

  if (!inflectionSchemeMap[language]) {
    return null;
  }

  if (!inflectionSchemeMap[language][wordclass]) {
    return null;
  }

  return inflectionSchemeMap[language][wordclass];
});

const extraInflectionTable = ref<DictsysInflectionSchemeTable>();

const onToggleExtraTable = async (table: DictsysInflectionSchemeTable) => {
  extraInflectionTable.value = table;

  await nextTick();

  const scrollBehavior = prefersReducedMotion.value ? "instant" : "smooth";
  document
    .querySelector(`#${extraInflectionTableId.value}`)
    ?.scrollIntoView({ behavior: scrollBehavior, block: "center", inline: "center" });
};
</script>

<template>
  <div v-if="isSchemeOpen(props.schemeId)" class="le-article-inflection-scheme">
    <div class="le-article-inflection-scheme__header">
      <LeHeading tag="h4" lang="da">Bøjningsskema</LeHeading>
      <LeIconButton size="s" type="button" aria-label="Luk bøjningsskema" @click="closeScheme(props.schemeId)">
        <LeCross role="presentation" />
      </LeIconButton>
    </div>
    <Component
      :is="inflectionSchemeComponent"
      :lemma="getHeadWord(props.schemeId)"
      :inflection-scheme="getScheme(props.schemeId).json"
      :language="props.language"
      class="le-article-inflection-scheme__body"
      @toggle-extra-table="onToggleExtraTable($event)"
    />
    <LeExtraInflectionScheme
      v-if="extraInflectionTable"
      :id="extraInflectionTableId"
      class="le-article-inflection-scheme__body le-article-inflection-scheme__body--extra"
      :table="extraInflectionTable"
      :language="props.language"
      :heading="extraInflectionTable.extraTableHeading"
    />
    <LeLegacyInflectionScheme
      v-if="inflectionSchemeComponent === null"
      class="le-article-inflection-scheme__body"
      :inflection-scheme-html="getScheme(props.schemeId).html"
    />
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-article-inflection-scheme {
  border: 1px solid @colors[@secondary][300];
  border-radius: 4px;
  background-color: @colors[@secondary][100];

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 68px;
    padding: 20px 30px;
    background-color: @colors[@neutrals][100];
  }

  &__body {
    width: 100%;
    padding: 6px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: -ms-autohiding-scrollbar;

    #media-breakpoint-up(lg, {
      padding: 30px;
    });

    &--extra {
      background: @colors[@secondary][200];
    }
  }
}
</style>
